// TVDisplay.jsx  (src/pages/tv/TVDisplay.jsx)

import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  useContext,
  createContext,
} from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  AreaChart,
  Area,
} from "recharts";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const POLL_INTERVAL_MS = 30_000;
const STALE_THRESHOLD_MS = 90_000;
const HEARTBEAT_INTERVAL_MS = 30_000;
const SPOTLIGHT_INTERVAL_MS = 12_000;

// ─── Design tokens ────────────────────────────────────────────────────────────

const PALETTE = [
  "#818cf8",
  "#34d399",
  "#fb923c",
  "#f472b6",
  "#38bdf8",
  "#a78bfa",
  "#facc15",
  "#4ade80",
];

const GRADIENT_PAIRS = [
  ["#818cf8", "#a78bfa"],
  ["#34d399", "#10b981"],
  ["#fb923c", "#f472b6"],
  ["#38bdf8", "#818cf8"],
];

const BG = "#111827";
const TAKEOVER_AUDIO_URL = "/sounds/takeover.mp3";
const BOOKING_AUDIO_URL = "/sounds/cheer.mp3";
const BOO_AUDIO_URL = "/sounds/boo.mp3";

// ─── Widget event bus ─────────────────────────────────────────────────────────
// Every widget subscribes to this context. TVDisplayPage publishes events
// (new-booking, new-sale) by updating the provider's value.

const WidgetEventContext = createContext(null);

// ─── Audio engine ─────────────────────────────────────────────────────────────

let _audioCtx = null;
function getAudioCtx() {
  if (_audioCtx) return _audioCtx;
  try {
    _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  } catch (_) {
    _audioCtx = null;
  }
  return _audioCtx;
}

function playTone(
  freq,
  duration = 0.18,
  vol = 0.025,
  type = "sine",
  delay = 0,
) {
  const ctx = getAudioCtx();
  if (!ctx) return;
  const t0 = ctx.currentTime + delay;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 4000;
  osc.type = type;
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(vol, t0 + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
  osc.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + duration + 0.05);
}

function playUpdate() {
  playTone(523.25, 0.22, 0.022);
  playTone(659.25, 0.22, 0.018, "sine", 0.04);
  playTone(783.99, 0.28, 0.014, "sine", 0.08);
}

function playSpotlight() {
  playTone(880, 0.12, 0.012, "sine");
  playTone(1318.5, 0.16, 0.008, "sine", 0.06);
}

function playScreenChange() {
  playTone(440, 0.15, 0.01, "sine");
  playTone(660, 0.18, 0.008, "sine", 0.04);
}

// Takeover-style fanfare for new bookings — rising chord + sparkle
// Takeover-style fanfare for new bookings — rising chord + sparkle
function playWidgetBookingAlert() {
  // Limit sound duration to 5-7 seconds max
  const MAX_DURATION_MS = 7000;
  const MIN_DURATION_MS = 5000;

  if (BOOKING_AUDIO_URL) {
    try {
      const audio = new Audio(BOOKING_AUDIO_URL);
      audio.volume = 0.85;

      // Create a timeout to stop the audio after 5-7 seconds
      const stopTimeout = setTimeout(
        () => {
          if (!audio.paused) {
            audio.pause();
            audio.currentTime = 0;
          }
        },
        Math.floor(
          Math.random() * (MAX_DURATION_MS - MIN_DURATION_MS + 1) +
            MIN_DURATION_MS,
        ),
      );

      // Clear timeout if audio ends naturally (before limit)
      audio.addEventListener("ended", () => clearTimeout(stopTimeout));
      audio.addEventListener("error", () => clearTimeout(stopTimeout));

      audio.play().catch(() => {});
      return;
    } catch (_) {}
  }

  // Fallback: play short synthesized tones if no URL (these are already short ~0.5-1s)
  if (typeof playTone !== "undefined") {
    playTone(880, 0.5, 0.025, "sine"); // A5
    playTone(1108.73, 0.6, 0.022, "sine", 0.08); // C#6
    playTone(1318.51, 0.7, 0.02, "sine", 0.16); // E6
    playTone(1760, 0.8, 0.018, "sine", 0.28); // A6
  }
}

// Bigger alert for a closed sale — used by new-sale event
function playSaleAlert() {
  if (TAKEOVER_AUDIO_URL) {
    try {
      const audio = new Audio(TAKEOVER_AUDIO_URL);
      audio.volume = 0.85;
      audio.play().catch(() => {});
      return;
    } catch (_) {}
  }
  playWidgetBookingAlert();
}

// Boo sound for missed contact-timer cases
function playBooAlert() {
  if (BOO_AUDIO_URL) {
    try {
      const audio = new Audio(BOO_AUDIO_URL);
      audio.volume = 0.9;
      audio.play().catch(() => {});
      return;
    } catch (_) {}
  }
  // Fallback: descending dissonant tones
  playTone(220, 0.6, 0.03, "sawtooth");
  playTone(196, 0.7, 0.025, "sawtooth", 0.12);
  playTone(174.61, 0.8, 0.02, "sawtooth", 0.28);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatValue(value, type) {
  if (value == null || value === "") return "—";
  const num = parseFloat(value);
  if (isNaN(num) && type !== "string") return value;
  if (type === "currency")
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(num);
  if (type === "percentage") return `${num.toFixed(1)}%`;
  if (type === "number") {
    if (Math.abs(num) >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
    if (Math.abs(num) >= 10_000) return `${(num / 1000).toFixed(1)}K`;
    return new Intl.NumberFormat("en-US").format(num);
  }
  return value;
}

// ─── Card ─────────────────────────────────────────────────────────────────────

function Card({ children, flash, spotlight, accent = "#818cf8" }) {
  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        background:
          flash || spotlight
            ? `linear-gradient(135deg, ${accent}14, rgba(255,255,255,0.02))`
            : "rgba(255,255,255,0.025)",
        border: `1px solid ${flash || spotlight ? accent + "55" : "rgba(255,255,255,0.06)"}`,
        borderRadius: 18,
        display: "flex",
        flexDirection: "column",
        padding: "16px 20px",
        overflow: "hidden",
        transition:
          "background 0.7s cubic-bezier(0.4,0,0.2,1), border-color 0.7s cubic-bezier(0.4,0,0.2,1), box-shadow 0.7s cubic-bezier(0.4,0,0.2,1), transform 0.5s cubic-bezier(0.34,1.56,0.64,1)",
        backdropFilter: "blur(14px) saturate(140%)",
        WebkitBackdropFilter: "blur(14px) saturate(140%)",
        boxShadow:
          flash || spotlight
            ? `0 0 40px ${accent}33, 0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.08)`
            : "0 4px 16px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.04)",
        transform: spotlight ? "scale(1.015)" : "scale(1)",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 1,
          background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
          opacity: flash || spotlight ? 1 : 0,
          transition: "opacity 0.7s ease",
        }}
      />
      {children}
    </div>
  );
}

function WidgetTitle({ children, live }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 10,
        flexShrink: 0,
      }}
    >
      <span
        style={{
          fontSize: "0.68rem",
          fontWeight: 600,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "rgba(255,255,255,0.4)",
        }}
      >
        {children}
      </span>
      {live && (
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: "#34d399",
            boxShadow: "0 0 8px #34d399",
            animation: "livePulse 2s ease-in-out infinite",
          }}
        />
      )}
    </div>
  );
}

function useFlash(data) {
  const [flash, setFlash] = useState(false);
  const prev = useRef(null);
  useEffect(() => {
    const key = JSON.stringify(data);
    if (prev.current && prev.current !== key) {
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 1400);
      return () => clearTimeout(t);
    }
    prev.current = key;
  }, [data]);
  return flash;
}

function AnimatedNumber({ value, format, style }) {
  const [display, setDisplay] = useState(() => formatValue(value, format));
  const raf = useRef(null);
  const startRef = useRef(null);
  const fromRef = useRef(parseFloat(value) || 0);

  useEffect(() => {
    const to = parseFloat(value) || 0;
    const from = fromRef.current;
    if (from === to) {
      setDisplay(formatValue(to, format));
      return;
    }
    const duration = 1100;
    cancelAnimationFrame(raf.current);
    startRef.current = null;
    const step = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const p = Math.min((ts - startRef.current) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 4);
      const cur = from + (to - from) * eased;
      setDisplay(formatValue(cur, format));
      if (p < 1) raf.current = requestAnimationFrame(step);
      else {
        fromRef.current = to;
        setDisplay(formatValue(to, format));
      }
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [value, format]);

  return <span style={style}>{display}</span>;
}

function useTrend(value) {
  const [history, setHistory] = useState([]);
  useEffect(() => {
    const num = parseFloat(value);
    if (isNaN(num)) return;
    setHistory((h) => [...h.slice(-11), { v: num }]);
  }, [value]);
  return history;
}

const tooltipStyle = {
  background: "#fff",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 10,
  fontSize: 12,
  boxShadow: "0 12px 32px rgba(0,0,0,0.6)",
  backdropFilter: "blur(8px)",
};

// ─── WIDGET BOOKING BURST ─────────────────────────────────────────────────────
// Full-bleed overlay that sits inside each Card. Shows picture + "New
// Appointment" label + booker name + company. Enters with spring physics,
// exits smoothly. Styling varies by event kind (booking = green, sale = gold).

function WidgetBookingBurst({ event, delay = 0, onDone }) {
  const [phase, setPhase] = useState("pre"); // pre → enter → exit

  useEffect(() => {
    const enter = setTimeout(() => setPhase("enter"), delay);
    const exit = setTimeout(() => setPhase("exit"), delay + 2600);
    const done = setTimeout(() => onDone?.(), delay + 3200);
    return () => {
      clearTimeout(enter);
      clearTimeout(exit);
      clearTimeout(done);
    };
  }, [delay, onDone]);

  const visible = phase === "enter";

  const isSale = event.kind === "sale";
  const accent = isSale ? "#facc15" : "#34d399";
  const accentDeep = isSale ? "#eab308" : "#10b981";
  const label = isSale ? "🔥 NEW SALE" : "🎉 NEW APPOINTMENT";

  // Support both booking and sale payload shapes from the backend
  const personName =
    event.bookerName || event.closerName || event.sellerName || "Someone";
  const personImage =
    event.bookerImageUrl ||
    event.closerImageUrl ||
    event.sellerImageUrl ||
    null;
  const subLine = event.companyName || event.customerName || event.title || "";

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 30,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        padding: 12,
        borderRadius: 18,
        pointerEvents: "none",
        overflow: "hidden",
        background: visible
          ? `linear-gradient(135deg, ${accent}55, ${accentDeep}33, rgba(17,24,39,0.55))`
          : "transparent",
        backdropFilter: visible ? "blur(10px) saturate(160%)" : "blur(0px)",
        WebkitBackdropFilter: visible
          ? "blur(10px) saturate(160%)"
          : "blur(0px)",
        transition:
          "background 0.45s cubic-bezier(0.4,0,0.2,1), backdrop-filter 0.45s ease",
      }}
    >
      {/* Glow radial */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `radial-gradient(circle at 50% 45%, ${accent}44 0%, transparent 65%)`,
          opacity: visible ? 1 : 0,
          transition: "opacity 0.4s ease",
        }}
      />

      {/* Radiating particles */}
      {visible &&
        Array.from({ length: 6 }).map((_, i) => {
          const angle = (i / 6) * 360;
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                width: 4,
                height: 4,
                borderRadius: "50%",
                background: accent,
                top: "50%",
                left: "50%",
                boxShadow: `0 0 8px ${accent}`,
                animation: `widgetBurstParticle 1.4s cubic-bezier(0.16,1,0.3,1) ${0.1 + i * 0.04}s both`,
                "--angle": `${angle}deg`,
              }}
            />
          );
        })}

      {/* Label banner */}
      <div
        style={{
          position: "relative",
          fontSize: "clamp(0.5rem, 1.1vw, 0.72rem)",
          fontWeight: 800,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          color: accent,
          fontFamily: "'DM Mono', monospace",
          textShadow: `0 0 14px ${accent}cc`,
          transform: visible ? "translateY(0)" : "translateY(-12px)",
          opacity: visible ? 1 : 0,
          transition:
            "transform 0.45s cubic-bezier(0.34,1.56,0.64,1), opacity 0.4s ease",
        }}
      >
        {label}
      </div>

      {/* Image */}
      {personImage && (
        <div
          style={{
            position: "relative",
            transform: visible ? "scale(1)" : "scale(0.55)",
            opacity: visible ? 1 : 0,
            transition:
              "transform 0.55s cubic-bezier(0.34,1.56,0.64,1) 0.06s, opacity 0.45s ease 0.06s",
          }}
        >
          <img
            src={personImage}
            alt={personName}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
            style={{
              width: "clamp(44px, 7vw, 78px)",
              height: "clamp(44px, 7vw, 78px)",
              borderRadius: "50%",
              objectFit: "cover",
              border: `3px solid ${accent}`,
              boxShadow: `0 0 0 4px ${accent}33, 0 0 28px ${accent}aa`,
              display: "block",
            }}
          />
        </div>
      )}

      {/* Name */}
      <div
        style={{
          position: "relative",
          fontSize: "clamp(0.85rem, 1.9vw, 1.2rem)",
          fontWeight: 800,
          color: "#ffffff",
          letterSpacing: "-0.02em",
          textAlign: "center",
          lineHeight: 1.1,
          maxWidth: "95%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          textShadow: "0 2px 12px rgba(0,0,0,0.4)",
          transform: visible ? "translateY(0)" : "translateY(10px)",
          opacity: visible ? 1 : 0,
          transition:
            "transform 0.5s cubic-bezier(0.34,1.56,0.64,1) 0.1s, opacity 0.45s ease 0.1s",
        }}
      >
        {personName}
      </div>

      {/* Company / detail */}
      {subLine && (
        <div
          style={{
            position: "relative",
            fontSize: "clamp(0.58rem, 1.15vw, 0.78rem)",
            fontWeight: 500,
            color: "rgba(255,255,255,0.85)",
            textAlign: "center",
            maxWidth: "95%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            transform: visible ? "translateY(0)" : "translateY(8px)",
            opacity: visible ? 1 : 0,
            transition:
              "transform 0.45s cubic-bezier(0.34,1.56,0.64,1) 0.14s, opacity 0.4s ease 0.14s",
          }}
        >
          {subLine}
        </div>
      )}
    </div>
  );
}

// ─── WIDGET BOO OVERLAY ───────────────────────────────────────────────────────
// Full-screen takeover for missed contact-timer cases.
// Shows the case owner's picture, a shaking red border, drip particles, and
// a big "BOO!" text. Plays boo.mp3 on entry.

function WidgetBooOverlay({ event, onDone }) {
  const [phase, setPhase] = useState("pre"); // pre → enter → hold → exit

  useEffect(() => {
    const enter = setTimeout(() => setPhase("enter"), 50);
    const hold = setTimeout(() => setPhase("hold"), 400);
    const exit = setTimeout(() => setPhase("exit"), 5500);
    const done = setTimeout(() => onDone?.(), 6200);
    return () => {
      clearTimeout(enter);
      clearTimeout(hold);
      clearTimeout(exit);
      clearTimeout(done);
    };
  }, [onDone]);

  const visible = phase === "enter" || phase === "hold";
  const entering = phase === "enter";

  const personName = event.ownerName || event.agentName || "Unknown";
  const personImage = event.ownerImageUrl || event.agentImageUrl || null;
  const caseRef = event.caseNumber
    ? `Case #${event.caseNumber}`
    : event.subject || "";

  const DRIPS = Array.from({ length: 8 }, (_, i) => ({
    angle: (i / 8) * 360,
    delay: 0.15 + i * 0.06,
    drop: `${28 + (i % 3) * 12}px`,
    height: 14 + (i % 4) * 6,
  }));

  const RINGS = Array.from({ length: 3 }, (_, i) => ({ delay: i * 0.55 }));

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1100,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 18,
        pointerEvents: "none",
        background: visible ? "rgba(20,0,0,0.82)" : "rgba(0,0,0,0)",
        backdropFilter: visible ? "blur(10px) saturate(140%)" : "blur(0px)",
        WebkitBackdropFilter: visible
          ? "blur(10px) saturate(140%)"
          : "blur(0px)",
        transition: "background 0.5s ease, backdrop-filter 0.5s ease",
        overflow: "hidden",
      }}
    >
      {/* Red vignette sweep */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse at center, transparent 30%, rgba(180,0,0,0.45) 100%)",
          opacity: visible ? 1 : 0,
          transition: "opacity 0.6s ease",
          pointerEvents: "none",
        }}
      />

      {/* Expanding pulse rings */}
      {visible &&
        RINGS.map((r, i) => (
          <div
            key={i}
            style={{
              position: "absolute",
              width: "clamp(120px, 22vw, 260px)",
              height: "clamp(120px, 22vw, 260px)",
              borderRadius: "50%",
              border: "3px solid rgba(220,30,30,0.7)",
              animation: `booRing 1.8s ease-out ${r.delay}s infinite`,
              pointerEvents: "none",
            }}
          />
        ))}

      {/* Avatar + drips */}
      <div
        style={{
          position: "relative",
          transform: visible
            ? entering
              ? "scale(0.6)"
              : "scale(1)"
            : "scale(0.5)",
          opacity: visible ? 1 : 0,
          transition:
            "transform 0.5s cubic-bezier(0.34,1.56,0.64,1), opacity 0.4s ease",
          animation:
            visible && !entering
              ? "booShake 0.55s cubic-bezier(0.36,0.07,0.19,0.97) 0.5s 2 both"
              : undefined,
        }}
      >
        {/* Drip droplets */}
        {visible &&
          DRIPS.map((d, i) => (
            <div
              key={i}
              style={{
                position: "absolute",
                width: 6,
                height: d.height,
                borderRadius: "0 0 4px 4px",
                background: "linear-gradient(to bottom, #cc0000, #7a0000)",
                top: "50%",
                left: "50%",
                transformOrigin: "top center",
                "--drop": d.drop,
                animation: `booDroplet 1.1s ease-in ${d.delay}s both`,
                transform: `rotate(${d.angle}deg) translateX(-50%)`,
              }}
            />
          ))}

        {/* Avatar */}
        {personImage ? (
          <img
            src={personImage}
            alt={personName}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
            style={{
              width: "clamp(90px, 16vw, 180px)",
              height: "clamp(90px, 16vw, 180px)",
              borderRadius: "50%",
              objectFit: "cover",
              border: "5px solid #cc0000",
              boxShadow:
                "0 0 0 8px rgba(200,0,0,0.25), 0 0 60px rgba(200,0,0,0.6), 0 0 120px rgba(200,0,0,0.25)",
              display: "block",
              filter: "saturate(80%) contrast(1.1)",
              animation: visible
                ? "booFlicker 2.4s ease-in-out 0.8s 1"
                : undefined,
            }}
          />
        ) : (
          <div
            style={{
              width: "clamp(90px, 16vw, 180px)",
              height: "clamp(90px, 16vw, 180px)",
              borderRadius: "50%",
              background: "radial-gradient(circle, #3a0000, #1a0000)",
              border: "5px solid #cc0000",
              boxShadow: "0 0 60px rgba(200,0,0,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "clamp(2.5rem, 6vw, 5rem)",
            }}
          >
            👻
          </div>
        )}
      </div>

      {/* BOO! text */}
      <div
        style={{
          fontSize: "clamp(3.5rem, 11vw, 9rem)",
          fontWeight: 900,
          fontFamily: "'DM Sans', sans-serif",
          letterSpacing: "-0.04em",
          lineHeight: 1,
          color: "#ff1a1a",
          textShadow:
            "0 0 20px rgba(255,30,30,0.9), 0 0 60px rgba(200,0,0,0.6), 0 4px 0 #7a0000",
          opacity: visible ? 1 : 0,
          animation: visible
            ? "booTextPop 0.55s cubic-bezier(0.34,1.56,0.64,1) 0.15s both"
            : undefined,
        }}
      >
        BOO!
      </div>

      {/* Agent name */}
      <div
        style={{
          fontSize: "clamp(1rem, 2.5vw, 1.6rem)",
          fontWeight: 800,
          color: "#ffffff",
          letterSpacing: "-0.02em",
          textAlign: "center",
          maxWidth: "75vw",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          textShadow: "0 2px 16px rgba(0,0,0,0.6)",
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(12px)",
          transition:
            "opacity 0.45s ease 0.25s, transform 0.5s cubic-bezier(0.34,1.56,0.64,1) 0.25s",
        }}
      >
        {personName}
      </div>

      {/* Missed contact subtitle */}
      <div
        style={{
          fontSize: "clamp(0.65rem, 1.4vw, 0.95rem)",
          fontWeight: 600,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: "#ff6666",
          textAlign: "center",
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(8px)",
          transition: "opacity 0.4s ease 0.35s, transform 0.45s ease 0.35s",
        }}
      >
        ⏰ Missed Contact Timer{caseRef ? ` · ${caseRef}` : ""}
      </div>
    </div>
  );
}

// ─── KPI ──────────────────────────────────────────────────────────────────────

function KPIWidget({ widget, data, spotlight }) {
  const { config } = widget;
  const metricField = widget.dataSource?.metricField;
  const rows = data?.result?.data || [];
  const aggregates = data?.result?.aggregates || {};
  const rawValue = metricField
    ? aggregates[metricField]
    : rows[0]
      ? Object.values(rows[0])[0]
      : null;

  const label = config?.label || widget.title || metricField || "Metric";
  const format = config?.format || "number";
  const flash = useFlash(rawValue);
  const trend = useTrend(rawValue);

  const threshold = config?.threshold;
  let accentColor = "#818cf8";
  if (threshold && rawValue != null) {
    const v = parseFloat(rawValue);
    accentColor =
      v >= threshold.good
        ? "#34d399"
        : v >= threshold.warn
          ? "#fb923c"
          : "#f87171";
  }

  const trendDelta =
    trend.length >= 2 ? trend[trend.length - 1].v - trend[0].v : 0;
  const trendUp = trendDelta > 0;
  const trendNeutral = trendDelta === 0;

  return (
    <Card flash={flash} spotlight={spotlight} accent={accentColor}>
      <WidgetTitle live={flash}>{label}</WidgetTitle>
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          minHeight: 0,
        }}
      >
        <AnimatedNumber
          value={rawValue}
          format={format}
          style={{
            fontSize: "clamp(2rem, 5.5vw, 4.5rem)",
            fontWeight: 800,
            color: accentColor,
            letterSpacing: "-0.04em",
            fontVariantNumeric: "tabular-nums",
            fontFamily: "'DM Mono', 'SF Mono', monospace",
            lineHeight: 1,
            textShadow: `0 0 24px ${accentColor}40`,
          }}
        />
        {trend.length >= 3 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              width: "100%",
              maxWidth: 200,
            }}
          >
            <div style={{ flex: 1, height: 24 }}>
              <ResponsiveContainer>
                <AreaChart data={trend}>
                  <defs>
                    <linearGradient
                      id={`spark-${widget.id}`}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="0%"
                        stopColor={accentColor}
                        stopOpacity={0.5}
                      />
                      <stop
                        offset="100%"
                        stopColor={accentColor}
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <Area
                    type="monotone"
                    dataKey="v"
                    stroke={accentColor}
                    strokeWidth={1.5}
                    fill={`url(#spark-${widget.id})`}
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <span
              style={{
                fontSize: "0.7rem",
                fontWeight: 600,
                color: trendNeutral
                  ? "rgba(255,255,255,0.4)"
                  : trendUp
                    ? "#34d399"
                    : "#f87171",
                fontFamily: "'DM Mono', monospace",
                minWidth: 32,
                textAlign: "right",
              }}
            >
              {trendNeutral ? "→" : trendUp ? "↑" : "↓"}{" "}
              {Math.abs(trendDelta) > 0 &&
                formatValue(Math.abs(trendDelta), format)}
            </span>
          </div>
        )}
      </div>
    </Card>
  );
}

// ─── Leaderboard ─────────────────────────────────────────────────────────────

function useNumberOne(rows, labelCol, onNewLeader) {
  const prevLeaderRef = useRef(null);
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!rows || rows.length === 0) return;
    const topRow = rows[0];
    const current = topRow?.[labelCol];
    if (!current) return;
    if (!initializedRef.current) {
      prevLeaderRef.current = current;
      initializedRef.current = true;
      return;
    }
    if (prevLeaderRef.current !== current) {
      onNewLeader(current, topRow?.profileImageUrl ?? null);
    }
    prevLeaderRef.current = current;
  }, [rows, labelCol]); // eslint-disable-line
}

function TakeoverOverlay({ name, imageUrl, onDone }) {
  const [phase, setPhase] = useState("enter");
  useEffect(() => {
    const hold = setTimeout(() => setPhase("exit"), 3000);
    const done = setTimeout(() => onDone(), 3700);
    return () => {
      clearTimeout(hold);
      clearTimeout(done);
    };
  }, [onDone]);
  const isVisible = phase !== "exit";
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: 24,
        pointerEvents: "none",
        background: isVisible ? "rgba(0,0,0,0.72)" : "rgba(0,0,0,0)",
        backdropFilter: isVisible ? "blur(8px)" : "blur(0px)",
        transition: "background 0.5s ease, backdrop-filter 0.5s ease",
      }}
    >
      <div
        style={{
          fontSize: "clamp(3rem, 8vw, 6rem)",
          lineHeight: 1,
          transform: isVisible
            ? "scale(1) translateY(0)"
            : "scale(0.4) translateY(-40px)",
          opacity: isVisible ? 1 : 0,
          transition:
            "transform 0.55s cubic-bezier(0.34,1.56,0.64,1), opacity 0.4s ease",
          filter: "drop-shadow(0 0 32px rgba(250,204,21,0.8))",
        }}
      >
        👑
      </div>
      <div
        style={{
          fontSize: "clamp(1rem, 3vw, 1.4rem)",
          fontWeight: 800,
          letterSpacing: "0.25em",
          textTransform: "uppercase",
          color: "#facc15",
          fontFamily: "'DM Mono', monospace",
          transform: isVisible ? "translateY(0)" : "translateY(20px)",
          opacity: isVisible ? 1 : 0,
          transition:
            "transform 0.5s cubic-bezier(0.34,1.56,0.64,1) 0.08s, opacity 0.4s ease 0.08s",
          textShadow: "0 0 40px rgba(250,204,21,0.6)",
        }}
      >
        NEW #1
      </div>
      {imageUrl && (
        <img
          src={imageUrl}
          alt={name}
          style={{
            width: "clamp(80px, 14vw, 160px)",
            height: "clamp(80px, 14vw, 160px)",
            borderRadius: "50%",
            objectFit: "cover",
            border: "4px solid #facc15",
            boxShadow:
              "0 0 0 6px rgba(250,204,21,0.2), 0 0 60px rgba(250,204,21,0.5)",
            transform: isVisible ? "scale(1)" : "scale(0.6)",
            opacity: isVisible ? 1 : 0,
            transition:
              "transform 0.55s cubic-bezier(0.34,1.56,0.64,1) 0.12s, opacity 0.4s ease 0.12s",
          }}
        />
      )}
      <div
        style={{
          fontSize: "clamp(1.5rem, 7vw, 6rem)",
          fontWeight: 800,
          color: "#ffffff",
          fontFamily: "'DM Sans', sans-serif",
          letterSpacing: "-0.03em",
          lineHeight: 1.05,
          textAlign: "center",
          maxWidth: "80vw",
          transform: isVisible
            ? "scale(1) translateY(0)"
            : "scale(1.1) translateY(30px)",
          opacity: isVisible ? 1 : 0,
          transition:
            "transform 0.6s cubic-bezier(0.34,1.56,0.64,1) 0.15s, opacity 0.4s ease 0.15s",
        }}
      >
        {name}
      </div>
      <div
        style={{
          fontSize: "clamp(0.85rem, 2vw, 1.1rem)",
          fontWeight: 500,
          color: "rgba(255,255,255,0.5)",
          letterSpacing: "0.15em",
          textTransform: "uppercase",
          opacity: isVisible ? 1 : 0,
          transition: "opacity 0.4s ease 0.28s",
        }}
      >
        Takes the top spot
      </div>
    </div>
  );
}

function LeaderboardWidget({ widget, data, spotlight, audioEnabled }) {
  const rows = data?.result?.data || [];
  const cols = data?.result?.columns || widget.dataSource?.columns || [];
  const META_KEYS = new Set(["profileImageUrl"]);
  const allKeys = Object.keys(rows[0] || {}).filter((k) => !META_KEYS.has(k));
  const numericKey = allKeys.find((k) => !isNaN(parseFloat(rows[0]?.[k])));
  const labelKey = allKeys.find((k) => k !== numericKey);
  const visible = widget.config?.visibleColumns || [];
  const labelCol =
    widget.dataSource?.labelField ||
    visible.find((f) => f !== numericKey) ||
    cols.find((c) => !c.type || c.type === "string")?.field ||
    labelKey ||
    allKeys[0];
  const valueCol =
    widget.dataSource?.metricField ||
    visible.find((f) =>
      ["number", "currency", "percentage"].includes(
        cols.find((c) => c.field === f)?.type,
      ),
    ) ||
    cols.find((c) => ["number", "currency", "percentage"].includes(c.type))
      ?.field ||
    numericKey ||
    allKeys[1];
  const format = cols.find((c) => c.field === valueCol)?.type || "number";
  const flash = useFlash(rows);

  const [takeoverName, setTakeoverName] = useState(null);
  const [takeoverImage, setTakeoverImage] = useState(null);
  useNumberOne(rows, labelCol, (name, imageUrl) => {
    setTakeoverName(name);
    setTakeoverImage(imageUrl);
    if (audioEnabled) {
      // Use the big fanfare for new #1
      if (TAKEOVER_AUDIO_URL) {
        try {
          const audio = new Audio(TAKEOVER_AUDIO_URL);
          audio.volume = 0.85;
          audio.play().catch(() => {});
        } catch (_) {}
      }
    }
  });

  const medals = ["🥇", "🥈", "🥉"];

  return (
    <>
      {takeoverName && (
        <TakeoverOverlay
          name={takeoverName}
          imageUrl={takeoverImage}
          onDone={() => {
            setTakeoverName(null);
            setTakeoverImage(null);
          }}
        />
      )}
      <Card flash={flash} spotlight={spotlight}>
        <WidgetTitle live={flash}>{widget.title}</WidgetTitle>
        <div
          style={{
            flex: 1,
            overflowY: "hidden",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {rows.slice(0, 8).map((row, i) => {
            const val = parseFloat(row[valueCol]) || 0;
            const color = PALETTE[i % PALETTE.length];
            const isTop = i === 0;
            return (
              <div
                key={`${row[labelCol]}-${i}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  animation: `slideIn 0.5s cubic-bezier(0.34,1.56,0.64,1) ${i * 0.06}s both`,
                  padding: isTop ? "4px 8px" : "2px 4px",
                  borderRadius: 8,
                  background: isTop ? `${color}10` : "transparent",
                  transition: "background 0.4s",
                }}
              >
                <span
                  style={{
                    fontSize: isTop ? "1rem" : "0.78rem",
                    width: 24,
                    flexShrink: 0,
                    color: i < 3 ? color : "rgba(255,255,255,0.4)",
                    fontWeight: 700,
                    textAlign: "center",
                  }}
                >
                  {medals[i] || `${i + 1}`}
                </span>
                <span
                  style={{
                    fontSize: "0.82rem",
                    color: isTop
                      ? "rgba(255,255,255,0.95)"
                      : "rgba(255,255,255,0.78)",
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontWeight: isTop ? 600 : 500,
                  }}
                >
                  {row[labelCol] || "—"}
                </span>
                <span
                  style={{
                    fontSize: "0.78rem",
                    color,
                    minWidth: 64,
                    textAlign: "right",
                    fontWeight: 700,
                    fontVariantNumeric: "tabular-nums",
                    fontFamily: "'DM Mono', monospace",
                    flexShrink: 0,
                  }}
                >
                  {formatValue(val, format)}
                </span>
              </div>
            );
          })}
        </div>
      </Card>
    </>
  );
}

// ─── Bar ──────────────────────────────────────────────────────────────────────

function BarChartWidget({ widget, data, spotlight }) {
  const rows = data?.result?.data || [];
  const cols = data?.result?.columns || [];
  const labelKey =
    widget.dataSource?.labelField ||
    cols[0]?.field ||
    Object.keys(rows[0] || {})[0];
  const valueKey =
    widget.dataSource?.metricField ||
    cols[1]?.field ||
    Object.keys(rows[0] || {})[1];
  const flash = useFlash(rows);
  if (!rows.length)
    return (
      <Card flash={false} spotlight={spotlight}>
        <WidgetTitle>{widget.title}</WidgetTitle>
        <EmptyState />
      </Card>
    );
  return (
    <Card flash={flash} spotlight={spotlight}>
      <WidgetTitle live={flash}>{widget.title}</WidgetTitle>
      <div style={{ flex: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={rows}
            margin={{ top: 4, right: 8, left: -20, bottom: 24 }}
          >
            <defs>
              {GRADIENT_PAIRS.map((pair, i) => (
                <linearGradient
                  key={i}
                  id={`barGrad-${widget.id}-${i}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={pair[0]} stopOpacity={1} />
                  <stop offset="100%" stopColor={pair[1]} stopOpacity={0.7} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="rgba(255,255,255,0.04)"
              vertical={false}
            />
            <XAxis
              dataKey={labelKey}
              tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              interval={0}
              angle={-25}
              textAnchor="end"
              height={40}
            />
            <YAxis
              tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              contentStyle={tooltipStyle}
              cursor={{ fill: "rgba(255,255,255,0.03)" }}
            />
            <Bar
              dataKey={valueKey}
              radius={[8, 8, 0, 0]}
              isAnimationActive
              animationDuration={1100}
              animationEasing="ease-out"
            >
              {rows.map((_, i) => (
                <Cell
                  key={i}
                  fill={`url(#barGrad-${widget.id}-${i % GRADIENT_PAIRS.length})`}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

// ─── Line ─────────────────────────────────────────────────────────────────────

function LineChartWidget({ widget, data, spotlight }) {
  const rows = data?.result?.data || [];
  const cols = data?.result?.columns || [];
  const labelKey =
    widget.dataSource?.labelField ||
    cols[0]?.field ||
    Object.keys(rows[0] || {})[0];
  const valueKey =
    widget.dataSource?.metricField ||
    cols[1]?.field ||
    Object.keys(rows[0] || {})[1];
  const flash = useFlash(rows);
  if (!rows.length)
    return (
      <Card flash={false} spotlight={spotlight}>
        <WidgetTitle>{widget.title}</WidgetTitle>
        <EmptyState />
      </Card>
    );
  return (
    <Card flash={flash} spotlight={spotlight}>
      <WidgetTitle live={flash}>{widget.title}</WidgetTitle>
      <div style={{ flex: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={rows}
            margin={{ top: 8, right: 8, left: -20, bottom: 24 }}
          >
            <defs>
              <linearGradient
                id={`lineGrad-${widget.id}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor="#818cf8" stopOpacity={0.5} />
                <stop offset="50%" stopColor="#818cf8" stopOpacity={0.15} />
                <stop offset="100%" stopColor="#818cf8" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="rgba(255,255,255,0.04)"
              vertical={false}
            />
            <XAxis
              dataKey={labelKey}
              tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              interval={0}
              angle={-25}
              textAnchor="end"
              height={40}
            />
            <YAxis
              tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip contentStyle={tooltipStyle} />
            <Area
              type="monotone"
              dataKey={valueKey}
              stroke="#a5b4fc"
              strokeWidth={2.5}
              fill={`url(#lineGrad-${widget.id})`}
              dot={{ fill: "#a5b4fc", r: 3, strokeWidth: 0 }}
              activeDot={{
                r: 6,
                fill: "#c7d2fe",
                stroke: "#818cf8",
                strokeWidth: 2,
              }}
              isAnimationActive
              animationDuration={1200}
              animationEasing="ease-out"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

// ─── Pie ──────────────────────────────────────────────────────────────────────

function renderCustomLabel({ cx, cy, midAngle, outerRadius, percent, name }) {
  if (percent < 0.04) return null;
  const RADIAN = Math.PI / 180;
  const radius = outerRadius * 1.18;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="rgba(255,255,255,0.7)"
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      fontSize={11}
      fontWeight={500}
    >
      {name} {(percent * 100).toFixed(0)}%
    </text>
  );
}

function PieWidget({ widget, data, spotlight }) {
  const rows = data?.result?.data || [];
  const cols = data?.result?.columns || [];
  const labelKey =
    widget.dataSource?.labelField ||
    cols[0]?.field ||
    Object.keys(rows[0] || {})[0];
  const valueKey =
    widget.dataSource?.metricField ||
    cols[1]?.field ||
    Object.keys(rows[0] || {})[1];
  const isDonut = widget.widgetType === "donut";
  const flash = useFlash(rows);
  if (!rows.length)
    return (
      <Card flash={false} spotlight={spotlight}>
        <WidgetTitle>{widget.title}</WidgetTitle>
        <EmptyState />
      </Card>
    );
  const pieData = rows
    .map((r) => ({ ...r, [valueKey]: parseFloat(r[valueKey]) || 0 }))
    .filter((r) => r[valueKey] > 0);
  const total = pieData.reduce((s, r) => s + r[valueKey], 0);
  return (
    <Card flash={flash} spotlight={spotlight}>
      <WidgetTitle live={flash}>{widget.title}</WidgetTitle>
      <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <defs>
              {PALETTE.map((c, i) => (
                <radialGradient
                  key={i}
                  id={`pieGrad-${widget.id}-${i}`}
                  cx="50%"
                  cy="50%"
                  r="50%"
                >
                  <stop offset="0%" stopColor={c} stopOpacity={1} />
                  <stop offset="100%" stopColor={c} stopOpacity={0.75} />
                </radialGradient>
              ))}
            </defs>
            <Pie
              data={pieData}
              dataKey={valueKey}
              nameKey={labelKey}
              innerRadius={isDonut ? "42%" : 0}
              outerRadius="72%"
              paddingAngle={isDonut ? 3 : 1}
              labelLine={false}
              label={renderCustomLabel}
              isAnimationActive
              animationBegin={0}
              animationDuration={1100}
            >
              {pieData.map((_, i) => (
                <Cell
                  key={i}
                  fill={`url(#pieGrad-${widget.id}-${i % PALETTE.length})`}
                  stroke="rgba(17,24,39,0.6)"
                  strokeWidth={2}
                />
              ))}
            </Pie>
            <Tooltip
              contentStyle={tooltipStyle}
              formatter={(val) => [formatValue(val, "number"), ""]}
            />
            {isDonut && (
              <>
                <text
                  x="50%"
                  y="48%"
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill="rgba(255,255,255,0.95)"
                  fontSize={20}
                  fontWeight={800}
                  fontFamily="'DM Mono', monospace"
                >
                  {formatValue(total, "number")}
                </text>
                <text
                  x="50%"
                  y="58%"
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill="rgba(255,255,255,0.4)"
                  fontSize={10}
                  fontWeight={500}
                  letterSpacing="0.1em"
                >
                  TOTAL
                </text>
              </>
            )}
          </PieChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

// ─── Table ────────────────────────────────────────────────────────────────────

function TableWidget({ widget, data, spotlight }) {
  const rows = data?.result?.data || [];
  const flash = useFlash(rows);
  const visible = widget.config?.visibleColumns;

  // Build the effective column list with a stable order.
  // Priority: saved widget config columns (stable, user-defined order) →
  //           response columns (may vary across refreshes) →
  //           keys derived from the first row (last resort, unstable).
  // Response columns are used only to enrich label/type metadata, never to set order.
  const configCols = widget.dataSource?.columns || [];
  const responseCols = data?.result?.columns || [];
  const responseColMap = new Map(responseCols.map((c) => [c.field, c]));

  let effectiveCols;
  if (configCols.length > 0) {
    // Use saved config order; merge in any updated label/type from the response
    effectiveCols = configCols.map((c) => ({
      ...c,
      ...(responseColMap.get(c.field) || {}),
    }));
  } else if (responseCols.length > 0) {
    // No saved config — freeze the first response we see; subsequent refreshes
    // will still use this same list because configCols will remain empty only
    // if the widget truly has no column config saved.
    effectiveCols = responseCols;
  } else {
    // Absolute fallback: derive from row keys (order may vary — acceptable only
    // when there's truly no column metadata anywhere)
    effectiveCols = Object.keys(rows[0] || {}).map((k) => ({
      field: k,
      label: k,
    }));
  }

  if (visible?.length)
    effectiveCols = effectiveCols.filter((c) => visible.includes(c.field));

  if (!effectiveCols.length && !rows.length)
    return (
      <Card flash={false} spotlight={spotlight}>
        <WidgetTitle>{widget.title}</WidgetTitle>
        <EmptyState />
      </Card>
    );
  return (
    <Card flash={flash} spotlight={spotlight}>
      <WidgetTitle live={flash}>{widget.title}</WidgetTitle>
      <div style={{ flex: 1, overflowY: "auto", marginRight: -4 }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "0.78rem",
          }}
        >
          <thead>
            <tr>
              {effectiveCols.map((c) => (
                <th
                  key={c.field}
                  style={{
                    padding: "8px 10px",
                    textAlign: "left",
                    color: "rgba(255,255,255,0.35)",
                    fontWeight: 600,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    fontSize: "0.62rem",
                    borderBottom: "1px solid rgba(255,255,255,0.08)",
                    position: "sticky",
                    top: 0,
                    background: "rgba(17,24,39,0.95)",
                    backdropFilter: "blur(8px)",
                  }}
                >
                  {c.label || c.field}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 20).map((row, i) => (
              <tr
                key={i}
                style={{
                  borderBottom: "1px solid rgba(255,255,255,0.04)",
                  animation: `fadeUp 0.4s ease ${i * 0.03}s both`,
                }}
              >
                {effectiveCols.map((c, ci) => (
                  <td
                    key={c.field}
                    style={{
                      padding: "8px 10px",
                      color:
                        ci === 0
                          ? "rgba(255,255,255,0.9)"
                          : "rgba(255,255,255,0.6)",
                      fontWeight: ci === 0 ? 600 : 400,
                      fontVariantNumeric: "tabular-nums",
                      fontFamily: ci > 0 ? "'DM Mono', monospace" : undefined,
                    }}
                  >
                    {formatValue(row[c.field], c.type)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ─── Goal ─────────────────────────────────────────────────────────────────────

function GoalWidget({ widget, data, spotlight }) {
  const { config } = widget;
  const metricField = widget.dataSource?.metricField;
  const aggregates = data?.result?.aggregates || {};
  const current = parseFloat(aggregates[metricField]) || 0;
  const target = parseFloat(config?.goal) || 100;
  const pct = Math.min((current / target) * 100, 100);
  const format = config?.format || "number";
  const flash = useFlash(current);
  const color = pct >= 100 ? "#34d399" : pct >= 70 ? "#fb923c" : "#818cf8";
  const reached = pct >= 100;
  return (
    <Card flash={flash} spotlight={spotlight} accent={color}>
      <WidgetTitle live={flash}>{widget.title}</WidgetTitle>
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 14,
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <AnimatedNumber
            value={current}
            format={format}
            style={{
              fontSize: "clamp(1.6rem, 3.8vw, 3rem)",
              fontWeight: 800,
              color,
              fontVariantNumeric: "tabular-nums",
              fontFamily: "'DM Mono', monospace",
              letterSpacing: "-0.03em",
              textShadow: `0 0 20px ${color}50`,
            }}
          />
          <span
            style={{
              color: "rgba(255,255,255,0.25)",
              fontSize: "1.4rem",
              fontWeight: 300,
            }}
          >
            /
          </span>
          <span
            style={{
              color: "rgba(255,255,255,0.5)",
              fontSize: "1.05rem",
              fontWeight: 500,
              fontFamily: "'DM Mono', monospace",
            }}
          >
            {formatValue(target, format)}
          </span>
        </div>
        <div
          style={{
            position: "relative",
            height: 10,
            background: "rgba(255,255,255,0.06)",
            borderRadius: 99,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${pct}%`,
              background: `linear-gradient(90deg, ${color}cc, ${color})`,
              borderRadius: 99,
              transition: "width 1.2s cubic-bezier(0.16,1,0.3,1)",
              boxShadow: `0 0 16px ${color}aa`,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)",
                animation: "shimmer 2.5s linear infinite",
              }}
            />
          </div>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: "0.8rem",
            color: "rgba(255,255,255,0.5)",
            fontWeight: 500,
          }}
        >
          <span>{pct.toFixed(0)}% complete</span>
          {reached && (
            <span
              style={{
                color: "#34d399",
                fontWeight: 700,
                animation: "celebrate 1s ease-in-out infinite alternate",
              }}
            >
              🎉 Goal reached
            </span>
          )}
        </div>
      </div>
    </Card>
  );
}

function EmptyState() {
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "rgba(255,255,255,0.18)",
        fontSize: "0.78rem",
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        fontWeight: 600,
      }}
    >
      No data
    </div>
  );
}

function LoadingWidget({ title }) {
  return (
    <Card flash={false}>
      <WidgetTitle>{title || "Loading"}</WidgetTitle>
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            border: "2px solid rgba(255,255,255,0.06)",
            borderTopColor: "#818cf8",
            borderRightColor: "#a78bfa",
            borderRadius: "50%",
            animation: "spin 0.9s linear infinite",
          }}
        />
      </div>
    </Card>
  );
}

// ─── Widget Router (with burst overlay integration) ───────────────────────────

function WidgetRenderer({ widget, spotlight, audioEnabled, widgetIndex = 0 }) {
  const data = widget.snapshot;
  const eventBus = useContext(WidgetEventContext);
  const [activeBurst, setActiveBurst] = useState(null);
  const lastEventKey = useRef(null);

  // Subscribe to event bus — trigger burst only on widgets that opted in
  useEffect(() => {
    if (!eventBus) return;
    if (eventBus.key === lastEventKey.current) return;
    lastEventKey.current = eventBus.key;
    if (!widget.config?.showBookingBurst) return;
    console.log(
      `[TV] Widget ${widget.id} received event: ${eventBus.kind} (key=${eventBus.key})`,
    );
    setActiveBurst(eventBus);
  }, [eventBus, widget.id, widget.config?.showBookingBurst]);

  // Stagger each widget by its grid position so the burst ripples across the screen
  const burstDelay = useMemo(() => {
    const pos = widget.position || { x: 0, y: 0 };
    // ~40ms per grid cell of x + 60ms per row of y, capped
    const raw = pos.x * 30 + pos.y * 60;
    return Math.min(raw, 600);
  }, [widget.position]);

  let inner;
  if (!data)
    inner = <LoadingWidget title={widget.title || widget.widgetType} />;
  else {
    switch (widget.widgetType) {
      case "kpi":
      case "number":
        inner = <KPIWidget widget={widget} data={data} spotlight={spotlight} />;
        break;
      case "leaderboard":
        inner = (
          <LeaderboardWidget
            widget={widget}
            data={data}
            spotlight={spotlight}
            audioEnabled={audioEnabled}
          />
        );
        break;
      case "bar_chart":
      case "funnel":
        inner = (
          <BarChartWidget widget={widget} data={data} spotlight={spotlight} />
        );
        break;
      case "line_chart":
        inner = (
          <LineChartWidget widget={widget} data={data} spotlight={spotlight} />
        );
        break;
      case "pie":
      case "donut":
        inner = <PieWidget widget={widget} data={data} spotlight={spotlight} />;
        break;
      case "table":
        inner = (
          <TableWidget widget={widget} data={data} spotlight={spotlight} />
        );
        break;
      case "goal":
      case "progress":
        inner = (
          <GoalWidget widget={widget} data={data} spotlight={spotlight} />
        );
        break;
      default:
        inner = (
          <Card flash={false} spotlight={spotlight}>
            <WidgetTitle>{widget.widgetType}</WidgetTitle>
            <EmptyState />
          </Card>
        );
    }
  }

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      {inner}
      {activeBurst && (
        <WidgetBookingBurst
          key={activeBurst.key}
          event={activeBurst}
          delay={burstDelay}
          onDone={() => setActiveBurst(null)}
        />
      )}
    </div>
  );
}

// ─── Status dot + Clock ───────────────────────────────────────────────────────

function StatusDot({ connected, isStale }) {
  const color = !connected || isStale ? "#ef4444" : "#34d399";
  const label = !connected
    ? "Reconnecting…"
    : isStale
      ? "Data may be delayed"
      : "Live";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: color,
          boxShadow: `0 0 10px ${color}, 0 0 20px ${color}80`,
          animation:
            connected && !isStale
              ? "livePulse 2s ease-in-out infinite"
              : "none",
          display: "inline-block",
        }}
      />
      <span
        style={{
          fontSize: "0.78rem",
          color: "rgba(255,255,255,0.55)",
          letterSpacing: "0.06em",
          fontWeight: 500,
        }}
      >
        {label}
      </span>
    </div>
  );
}

function Clock() {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <span
      style={{
        fontFamily: "'DM Mono', monospace",
        fontSize: "0.95rem",
        color: "rgba(255,255,255,0.6)",
        letterSpacing: "0.05em",
        fontWeight: 500,
      }}
    >
      {time.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
    </span>
  );
}

// ─── Global keyframes ─────────────────────────────────────────────────────────

const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=DM+Sans:wght@400;500;600;700;800&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #111827; }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes livePulse { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:0.5; transform:scale(0.85); } }
  @keyframes slideIn { from { opacity:0; transform:translateX(-12px); } to { opacity:1; transform:translateX(0); } }
  @keyframes fadeUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
  @keyframes shimmer { 0% { transform:translateX(-100%); } 100% { transform:translateX(100%); } }
  @keyframes celebrate { from { transform:scale(1); } to { transform:scale(1.05); } }
  @keyframes meshDrift { 0% { transform:translate(0,0) scale(1); } 33% { transform:translate(2%,-1%) scale(1.05); } 66% { transform:translate(-1%,2%) scale(0.98); } 100% { transform:translate(0,0) scale(1); } }
  @keyframes meshDrift2 { 0% { transform:translate(0,0) scale(1); } 50% { transform:translate(-3%,2%) scale(1.08); } 100% { transform:translate(0,0) scale(1); } }
  @keyframes float { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-8px); } }
  @keyframes screenFade { from { opacity:0; transform:scale(0.985); } to { opacity:1; transform:scale(1); } }
  @keyframes widgetBurstParticle {
    0%   { transform: translate(-50%,-50%) rotate(var(--angle)) translateX(0px); opacity:1; }
    100% { transform: translate(-50%,-50%) rotate(var(--angle)) translateX(90px); opacity:0; }
  }
  @keyframes booShake {
    0%,100% { transform: translateX(0) rotate(0deg); }
    10% { transform: translateX(-8px) rotate(-3deg); }
    20% { transform: translateX(8px) rotate(3deg); }
    30% { transform: translateX(-6px) rotate(-2deg); }
    40% { transform: translateX(6px) rotate(2deg); }
    50% { transform: translateX(-4px) rotate(-1deg); }
    60% { transform: translateX(4px) rotate(1deg); }
    70% { transform: translateX(-2px) rotate(0deg); }
  }
  @keyframes booTextPop {
    0%   { transform: scale(0.3) rotate(-8deg); opacity:0; }
    60%  { transform: scale(1.15) rotate(3deg); opacity:1; }
    80%  { transform: scale(0.95) rotate(-1deg); }
    100% { transform: scale(1) rotate(0deg); opacity:1; }
  }
  @keyframes booFlicker {
    0%,100% { opacity:1; } 8% { opacity:0.85; } 16% { opacity:1; } 24% { opacity:0.7; } 32% { opacity:1; }
  }
  @keyframes booDroplet {
    0%   { transform: translate(-50%,0) scaleY(0); opacity:1; }
    60%  { transform: translate(-50%,var(--drop)) scaleY(1); opacity:1; }
    100% { transform: translate(-50%,var(--drop)) scaleY(1); opacity:0; }
  }
  @keyframes booRing {
    0%   { transform: scale(0.6); opacity:0.8; }
    100% { transform: scale(2.2); opacity:0; }
  }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 2px; }
  ::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.2); }
`;

function GlobalStyles() {
  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = GLOBAL_CSS;
    document.head.appendChild(el);
    return () => el.remove();
  }, []);
  return null;
}

// ─── Ambient bg ───────────────────────────────────────────────────────────────

function AmbientBackground() {
  return (
    <>
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: 0,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "-15%",
            left: "-10%",
            width: "60%",
            height: "60%",
            background:
              "radial-gradient(circle, rgba(129,140,248,0.18) 0%, transparent 60%)",
            filter: "blur(40px)",
            animation: "meshDrift 18s ease-in-out infinite",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-20%",
            right: "-10%",
            width: "55%",
            height: "55%",
            background:
              "radial-gradient(circle, rgba(52,211,153,0.12) 0%, transparent 60%)",
            filter: "blur(50px)",
            animation: "meshDrift2 22s ease-in-out infinite",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "30%",
            right: "20%",
            width: "40%",
            height: "40%",
            background:
              "radial-gradient(circle, rgba(244,114,182,0.08) 0%, transparent 60%)",
            filter: "blur(50px)",
            animation: "meshDrift 25s ease-in-out infinite reverse",
          }}
        />
      </div>
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.012) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.012) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
          pointerEvents: "none",
          zIndex: 0,
          maskImage:
            "radial-gradient(ellipse at center, black 40%, transparent 90%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, black 40%, transparent 90%)",
        }}
      />
    </>
  );
}

// ─── Grid ─────────────────────────────────────────────────────────────────────

function TVGrid({ widgets, spotlightId, audioEnabled }) {
  const containerRef = useRef(null);
  const [rowPx, setRowPx] = useState(120);

  const rowCount = useMemo(() => {
    if (!widgets.length) return 4;
    return Math.max(
      ...widgets.map((w) => (w.position?.y ?? 0) + (w.position?.h ?? 2)),
    );
  }, [widgets]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const compute = () => {
      const GAP = 12,
        PADDING = 24;
      const h = el.clientHeight - PADDING - GAP * (rowCount - 1);
      setRowPx(Math.max(60, Math.floor(h / rowCount)));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [rowCount]);

  return (
    <div
      ref={containerRef}
      style={{
        flex: 1,
        padding: 12,
        overflow: "hidden",
        animation: "screenFade 0.6s cubic-bezier(0.16,1,0.3,1)",
        display: "grid",
        gridTemplateColumns: "repeat(12, 1fr)",
        gridTemplateRows: `repeat(${rowCount}, ${rowPx}px)`,
        gap: 12,
        alignContent: "start",
      }}
    >
      {widgets.map((widget, idx) => {
        const pos = widget.position || { x: 0, y: 0, w: 3, h: 2 };
        return (
          <div
            key={widget.id}
            style={{
              gridColumn: `${pos.x + 1} / span ${pos.w}`,
              gridRow: `${pos.y + 1} / span ${pos.h}`,
              minHeight: 0,
              animation: `fadeUp 0.6s cubic-bezier(0.34,1.56,0.64,1) ${idx * 0.05}s both`,
            }}
          >
            <WidgetRenderer
              widget={widget}
              spotlight={spotlightId === widget.id}
              audioEnabled={audioEnabled}
              widgetIndex={idx}
            />
          </div>
        );
      })}
    </div>
  );
}

// ─── Cycling screens ─────────────────────────────────────────────────────────

function CyclingScreens({
  widgets,
  dashboard,
  spotlightId,
  audioEnabled,
  paused,
}) {
  const screenMap = useMemo(() => {
    const map = new Map();
    widgets.forEach((w) => {
      const s = w.position?.screen ?? 0;
      if (!map.has(s)) map.set(s, []);
      map.get(s).push(w);
    });
    return map;
  }, [widgets]);

  const screenIndexes = useMemo(
    () => [...screenMap.keys()].sort((a, b) => a - b),
    [screenMap],
  );
  const theme = dashboard?.theme || {};
  const cycleEnabled =
    theme.cycleEnabled !== false && screenIndexes.length > 1 && !paused;
  const cycleIntervalMs = Math.max(
    5000,
    (parseInt(theme.cycleInterval, 10) || 30) * 1000,
  );
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (!cycleEnabled) return;
    const tick = setInterval(() => {
      setActiveIdx((i) => (i + 1) % screenIndexes.length);
      if (audioEnabled) playScreenChange();
    }, cycleIntervalMs);
    return () => clearInterval(tick);
  }, [cycleEnabled, cycleIntervalMs, screenIndexes.length, audioEnabled]);

  useEffect(() => {
    if (activeIdx >= screenIndexes.length && screenIndexes.length > 0)
      setActiveIdx(0);
  }, [screenIndexes.length, activeIdx]);

  if (screenIndexes.length === 0) {
    return (
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "rgba(255,255,255,0.3)",
          fontSize: "0.9rem",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          fontWeight: 600,
        }}
      >
        No widgets configured
      </div>
    );
  }

  const currentScreen = screenIndexes[activeIdx] ?? screenIndexes[0];
  const currentWidgets = screenMap.get(currentScreen) || [];

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        position: "relative",
        zIndex: 1,
      }}
    >
      <TVGrid
        key={currentScreen}
        widgets={currentWidgets}
        spotlightId={spotlightId}
        audioEnabled={audioEnabled}
      />
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function TVDisplayPage() {
  const shareToken = window.location.pathname.split("/tv/")[1]?.split("?")[0];
  const tvId = new URLSearchParams(window.location.search).get("tvId");

  const [dashboard, setDashboard] = useState(null);
  const [widgets, setWidgets] = useState([]);
  const [, setLastUpdated] = useState(null);
  const [isStale, setIsStale] = useState(false);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [spotlightId, setSpotlightId] = useState(null);
  const [audioEnabled, setAudioEnabled] = useState(false);

  // The single active event broadcast to every widget via context.
  // Changing `key` on each new event forces widgets to re-trigger.
  const [widgetEvent, setWidgetEvent] = useState(null);

  // Boo overlay state — fires when a missed-contact event arrives
  const [booEvent, setBooEvent] = useState(null);

  const sseRef = useRef(null);
  const pollRef = useRef(null);
  const staleTimerRef = useRef(null);
  const lastDataRef = useRef(null);

  // Refs that mirror state/props so SSE callbacks always see latest values
  // without needing to be recreated (and without breaking the EventSource
  // reconnect closure). This is the key fix for the silent-event bug.
  const audioEnabledRef = useRef(false);
  useEffect(() => {
    audioEnabledRef.current = audioEnabled;
  }, [audioEnabled]);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 80);
    return () => clearTimeout(t);
  }, []);

  // Unlock WebAudio on first user interaction (browser policy)
  useEffect(() => {
    const unlock = () => {
      const ctx = getAudioCtx();
      if (ctx && ctx.state === "suspended") ctx.resume();
      setAudioEnabled(true);
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
    };
    window.addEventListener("click", unlock);
    window.addEventListener("keydown", unlock);
    return () => {
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  const markFresh = useCallback(() => {
    const now = new Date();
    setLastUpdated(now);
    lastDataRef.current = now;
    setIsStale(false);
    clearTimeout(staleTimerRef.current);
    staleTimerRef.current = setTimeout(
      () => setIsStale(true),
      STALE_THRESHOLD_MS,
    );
  }, []);

  // applyWidgetUpdate no longer depends on audioEnabled — uses ref instead.
  const applyWidgetUpdate = useCallback(
    (widgetId, data) => {
      setWidgets((prev) => {
        const existing = prev.find((w) => w.id === widgetId);
        const changed =
          existing &&
          JSON.stringify(existing.snapshot) !== JSON.stringify(data);
        if (changed && audioEnabledRef.current) playUpdate();
        return prev.map((w) =>
          w.id === widgetId ? { ...w, snapshot: data } : w,
        );
      });
      markFresh();
    },
    [markFresh],
  );

  // Broadcast an event to all widgets (they'll burst)
  const broadcastWidgetEvent = useCallback((kind, payload) => {
    const event = {
      ...payload,
      kind,
      key: `${kind}-${Date.now()}-${Math.random()}`,
    };
    console.log("[TV] broadcastWidgetEvent →", event);
    setWidgetEvent(event);
  }, []);

  const poll = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/dashboards/public/${shareToken}`);
      if (!res.ok) throw new Error(`Poll failed: ${res.status}`);
      const json = await res.json();
      if (json.success) {
        setDashboard(json.data.dashboard);
        setWidgets((prev) => {
          const prevMap = Object.fromEntries(prev.map((w) => [w.id, w]));
          return json.data.widgets.map((incoming) => {
            const existing = prevMap[incoming.id];
            const snapshot =
              incoming.snapshot ?? existing?.snapshot ?? undefined;
            return { ...incoming, snapshot };
          });
        });
        markFresh();
      }
    } catch (e) {
      console.warn("Poll error:", e.message);
    }
  }, [shareToken, markFresh]);

  // connectSSE no longer depends on audioEnabled — uses audioEnabledRef.
  // This means: one stable function → one EventSource for the session,
  // and audio gets its latest value without reconnecting.
  const connectSSE = useCallback(() => {
    if (sseRef.current) sseRef.current.close();
    const url = `${API_BASE}/dashboards/public/${shareToken}/stream${tvId ? `?tvId=${tvId}` : ""}`;
    console.log("[TV] Opening EventSource →", url);
    const es = new EventSource(url);

    es.onopen = () => {
      console.log("[TV] ✅ EventSource onopen fired");
      setConnected(true);
      setError(null);
    };

    es.addEventListener("connected", (e) => {
      console.log("[TV] ✅ `connected` event received:", e.data);
      setConnected(true);
      setError(null);
    });

    es.addEventListener("widget-update", (e) => {
      try {
        const payload = JSON.parse(e.data);
        applyWidgetUpdate(payload.widgetId, payload.data);
      } catch (err) {
        console.error("[TV] widget-update handler failed:", err, e?.data);
      }
    });

    // ── NEW BOOKING — trigger widget-level bursts + play sound ONCE ──
    es.addEventListener("new-booking", (e) => {
      console.log("[TV] 🎉 new-booking event received:", e.data);
      try {
        const payload = JSON.parse(e.data);
        broadcastWidgetEvent("booking", payload);
        if (audioEnabledRef.current) {
          playWidgetBookingAlert();
        } else {
          console.log(
            "[TV] ⚠️  new-booking arrived but audioEnabled=false (user hasn't clicked yet — sound suppressed, visual will still show)",
          );
        }
      } catch (err) {
        console.error("[TV] new-booking handler failed:", err, e?.data);
      }
    });

    // ── NEW SALE — similar flow, different styling + bigger sound ──
    es.addEventListener("new-sale", (e) => {
      console.log("[TV] 🔥 new-sale event received:", e.data);
      try {
        const payload = JSON.parse(e.data);
        broadcastWidgetEvent("sale", payload);
        if (audioEnabledRef.current) {
          playSaleAlert();
        } else {
          console.log(
            "[TV] ⚠️  new-sale arrived but audioEnabled=false (sound suppressed, visual will still show)",
          );
        }
      } catch (err) {
        console.error("[TV] new-sale handler failed:", err, e?.data);
      }
    });

    // ── MISSED CONTACT — boo overlay + boo.mp3 ──
    es.addEventListener("missed-contact", (e) => {
      console.log("[TV] 👻 missed-contact event received:", e.data);
      try {
        const payload = JSON.parse(e.data);
        setBooEvent({ ...payload, key: `boo-${Date.now()}-${Math.random()}` });
        if (audioEnabledRef.current) {
          playBooAlert();
        } else {
          console.log(
            "[TV] ⚠️  missed-contact arrived but audioEnabled=false (sound suppressed, visual will still show)",
          );
        }
      } catch (err) {
        console.error("[TV] missed-contact handler failed:", err, e?.data);
      }
    });

    es.onerror = (e) => {
      console.warn("[TV] ⚠️  EventSource error — will reconnect in 5s", e);
      setConnected(false);
      es.close();
      setTimeout(() => connectSSE(), 5000);
    };
    sseRef.current = es;
  }, [shareToken, tvId, applyWidgetUpdate, broadcastWidgetEvent]);

  // Rotating spotlight across widgets (existing feature)
  useEffect(() => {
    if (!widgets.length) return;
    const tick = () => {
      setSpotlightId((prev) => {
        const ids = widgets.map((w) => w.id);
        if (!ids.length) return null;
        const idx = ids.indexOf(prev);
        const nextId = ids[(idx + 1) % ids.length];
        if (audioEnabledRef.current) playSpotlight();
        return nextId;
      });
    };
    const t = setInterval(tick, SPOTLIGHT_INTERVAL_MS);
    const initial = setTimeout(tick, 2000);
    return () => {
      clearInterval(t);
      clearTimeout(initial);
    };
  }, [widgets.length]);

  useEffect(() => {
    if (!shareToken) {
      setError("No share token in URL");
      return;
    }
    poll();
    connectSSE();
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);
    let heartbeatInterval;
    if (tvId) {
      heartbeatInterval = setInterval(() => {
        fetch(`${API_BASE}/dashboards/tv/${tvId}/heartbeat`, {
          method: "POST",
        }).catch(() => {});
      }, HEARTBEAT_INTERVAL_MS);
    }
    const reloadTimer = setTimeout(
      () => {
        const timeSinceData = lastDataRef.current
          ? Date.now() - lastDataRef.current.getTime()
          : Infinity;
        if (timeSinceData > 5 * 60 * 1000) window.location.reload();
      },
      5 * 60 * 1000,
    );
    return () => {
      sseRef.current?.close();
      clearInterval(pollRef.current);
      clearInterval(heartbeatInterval);
      clearTimeout(staleTimerRef.current);
      clearTimeout(reloadTimer);
    };
    // eslint-disable-next-line
  }, [shareToken]);

  if (error) {
    return (
      <>
        <GlobalStyles />
        <div
          style={{
            width: "100vw",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            overflow: "hidden",
            background: BG,
            color: "#f87171",
            fontFamily: "DM Sans, sans-serif",
          }}
        >
          <AmbientBackground />
          <div style={{ textAlign: "center", zIndex: 1, position: "relative" }}>
            <div
              style={{
                fontSize: "3rem",
                marginBottom: 16,
                animation: "float 3s ease-in-out infinite",
              }}
            >
              ⚠
            </div>
            <div style={{ fontSize: "1.15rem", fontWeight: 500 }}>{error}</div>
          </div>
        </div>
      </>
    );
  }

  if (!dashboard) {
    return (
      <>
        <GlobalStyles />
        <div
          style={{
            width: "100vw",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            overflow: "hidden",
            background: BG,
            fontFamily: "DM Sans, sans-serif",
          }}
        >
          <AmbientBackground />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 20,
              zIndex: 1,
              position: "relative",
            }}
          >
            <div
              style={{
                width: 48,
                height: 48,
                border: "2px solid rgba(255,255,255,0.06)",
                borderTopColor: "#818cf8",
                borderRightColor: "#a78bfa",
                borderRadius: "50%",
                animation: "spin 0.9s linear infinite",
              }}
            />
            <div
              style={{
                color: "rgba(255,255,255,0.4)",
                fontSize: "0.85rem",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                fontWeight: 600,
              }}
            >
              Connecting
            </div>
          </div>
        </div>
      </>
    );
  }

  const theme = dashboard.theme || {};

  return (
    <>
      <GlobalStyles />
      <WidgetEventContext.Provider value={widgetEvent}>
        <div
          style={{
            width: "100vw",
            height: "100vh",
            overflow: "hidden",
            background: BG,
            color: theme.textColor || "#f8fafc",
            fontFamily: "'DM Sans', system-ui, sans-serif",
            display: "flex",
            flexDirection: "column",
            opacity: mounted ? 1 : 0,
            transition: "opacity 0.6s ease",
            position: "relative",
          }}
        >
          <AmbientBackground />

          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              padding: "12px 24px",
              borderBottom: "1px solid rgba(255,255,255,0.05)",
              flexShrink: 0,
              position: "relative",
              zIndex: 2,
              background: "rgba(17,24,39,0.7)",
              backdropFilter: "blur(16px) saturate(140%)",
              WebkitBackdropFilter: "blur(16px) saturate(140%)",
            }}
          >
            {theme.logoUrl && (
              <img
                src={theme.logoUrl}
                alt="logo"
                style={{ height: 60, objectFit: "contain" }}
              />
            )}
            <span
              style={{
                fontWeight: 700,
                fontSize: "1rem",
                flex: 1,
                letterSpacing: "0.005em",
                color: "rgba(255,255,255,0.95)",
              }}
            >
              {dashboard.name}
            </span>
            <StatusDot connected={connected} isStale={isStale} />
            <Clock />
          </div>

          {/* Cycling screens — widget bursts render INSIDE each widget */}
          <CyclingScreens
            widgets={widgets}
            dashboard={dashboard}
            spotlightId={spotlightId}
            audioEnabled={audioEnabled}
            paused={false}
          />

          {/* Boo overlay — full-screen, fixed, on top of everything */}
          {booEvent && (
            <WidgetBooOverlay
              key={booEvent.key}
              event={booEvent}
              onDone={() => setBooEvent(null)}
            />
          )}
        </div>
      </WidgetEventContext.Provider>
    </>
  );
}
